import React, { useState, useCallback, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import AiLabPageShell from '@/components/ai-lab-page-shell/AiLabPageShell';
import { useQwenTTS } from './useQwenTTS';
import { TextInputPanel } from './text-input-panel/TextInputPanel';
import { VoiceControls } from './voice-controls/VoiceControls';
import { PlaybackPanel } from './playback-panel/PlaybackPanel';

// 与 aiedu demo 对齐：6 个描述性音色标签，映射到 Qwen3-TTS-Flash 非实时音色。
// 来源：https://help.aliyun.com/zh/model-studio/qwen-tts-voice-list#261bc7f209ri2
const VOICE_OPTIONS = [
  { label: '女声-自然', value: 'Cherry' },
  { label: '女声-温柔', value: 'Serena' },
  { label: '女声-活泼', value: 'Momo' },
  { label: '男声-阳光', value: 'Ethan' },
  { label: '男声-沉稳', value: 'Neil' },
  { label: '男声-爽朗', value: 'Moon' },
];

const DEFAULT_TEXT = '欢迎来到 AI 语音合成实验室！';
const DEFAULT_VOICE = 'Cherry';

function VoiceSynthesis() {
  const [text, setText] = useState(DEFAULT_TEXT);
  const [selectedVoice, setSelectedVoice] = useState(DEFAULT_VOICE);
  const [speechRate, setSpeechRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [volume, setVolume] = useState(0.75);
  const [selectedTemplateCat, setSelectedTemplateCat] = useState(null);

  const {
    isGenerating,
    isProcessingAudio,
    isPlaying,
    isPaused,
    audioUrl,
    audioRef,
    generate,
    play,
    stop,
    handleEnded,
    handlePause,
    handleDownload,
  } = useQwenTTS();

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume, audioRef]);

  const handleGenerate = useCallback(() => {
    const apiRate = Math.round((speechRate - 1) * 100);
    const apiPitch = Math.round((pitch - 1) * 100);
    generate(text, selectedVoice, 'Chinese', apiRate, apiPitch);
  }, [text, selectedVoice, speechRate, pitch, generate]);

  const handleReset = useCallback(() => {
    stop();
    setText(DEFAULT_TEXT);
    setSelectedVoice(DEFAULT_VOICE);
    setSpeechRate(1);
    setPitch(1);
    setVolume(0.75);
  }, [stop]);

  return (
    <AiLabPageShell title="AI语音合成">
      <div className="space-y-6 pb-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls */}
          <div className="space-y-4 lg:order-1">
            <VoiceControls
              selectedVoice={selectedVoice}
              setSelectedVoice={setSelectedVoice}
              voiceOptions={VOICE_OPTIONS}
              speechRate={speechRate}
              setSpeechRate={setSpeechRate}
              pitch={pitch}
              setPitch={setPitch}
              volume={volume}
              setVolume={setVolume}
              isGenerating={isGenerating}
              isProcessingAudio={isProcessingAudio}
              canGenerate={text.trim().length > 0 && text.trim().length <= 100}
              onGenerate={handleGenerate}
            />
          </div>

          {/* Text Input + Playback */}
          <div className="lg:col-span-2 flex flex-col gap-4 lg:order-2">
            <TextInputPanel
              text={text}
              setText={setText}
              selectedTemplateCat={selectedTemplateCat}
              setSelectedTemplateCat={setSelectedTemplateCat}
            />
            <PlaybackPanel
              isGenerating={isGenerating}
              isProcessingAudio={isProcessingAudio}
              isPlaying={isPlaying}
              isPaused={isPaused}
              audioUrl={audioUrl}
              onPlay={play}
              onStop={stop}
              onDownload={handleDownload}
              onReset={handleReset}
            />
          </div>
        </div>

        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          src={audioUrl || ''}
          onEnded={handleEnded}
          onPause={handlePause}
          style={{ display: 'none' }}
        />

        {/* Educational Tip */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border border-purple-100 p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-white border border-purple-100 flex items-center justify-center shrink-0">
            <Sparkles size={20} className="text-purple-600" />
          </div>
          <div>
            <h4 className="font-bold text-gray-800 mb-1">
              小知识：什么是语音合成？
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              语音合成（Text-to-Speech，简称
              TTS）是人工智能的重要应用之一。它能把文字变成真人一样的语音。
              通过调节<b>语速</b>、<b>音调</b>和<b>音色</b>
              ，我们可以让同一段话听起来像小朋友、机器人或者故事里的角色。
              这项技术已经广泛应用在智能导航、有声读物和语音助手里啦！
            </p>
          </div>
        </div>
      </div>
    </AiLabPageShell>
  );
}

export default VoiceSynthesis;
